import mitraUnenrollmentApi from '@/api/mitra-unenrollment/mitra-unenrollment';
import { normalizeBatchList } from '@/utils/batchIdentity';

const state = {
  courses: [],
  courseDetail: null,
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPage: 0
  },
  loading: false,
  error: null,
  // Batch related state
  batches: [],
  batchLoading: false,
  batchError: null,
  // Summary statistics
  summary: {
    this_year: {
      submitted: 0,
      rejected: 0,
      approved: 0,
      completed: 0,
      percentage: 0,
      total: 0
    },
    history: {
      submitted: 0,
      rejected: 0,
      approved: 0,
      completed: 0,
      percentage: 0,
      total: 0
    }
  },
  summaryLoading: false
};

const getters = {
  courses: state => state.courses || [],
  courseDetail: state => state.courseDetail || null,
  pagination: state => state.pagination || { total: 0, page: 1, limit: 10, totalPage: 0 },
  loading: state => state.loading || false,
  error: state => state.error || null,
  // Batch getters
  batches: state => state.batches || [],
  batchLoading: state => state.batchLoading || false,
  batchError: state => state.batchError || null,
  // Summary getters
  summary: state => state.summary || {
    this_year: { submitted: 0, rejected: 0, approved: 0, completed: 0, percentage: 0, total: 0 },
    history: { submitted: 0, rejected: 0, approved: 0, completed: 0, percentage: 0, total: 0 }
  },
  summaryLoading: state => state.summaryLoading || false
};

const mutations = {
  SET_COURSES(state, courses) {
    state.courses = courses;
  },
  SET_COURSE_DETAIL(state, courseDetail) {
    state.courseDetail = courseDetail;
  },
  SET_PAGINATION(state, pagination) {
    state.pagination = {
      total: pagination.total,
      page: pagination.page,
      limit: pagination.limit,
      totalPage: pagination.totalPage
    };
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_ERROR(state) {
    state.error = null;
  },
  // Batch mutations
  SET_BATCHES(state, batches) {
    state.batches = Array.isArray(batches) ? batches : [];
  },
  SET_BATCH_LOADING(state, loading) {
    state.batchLoading = loading;
  },
  SET_BATCH_ERROR(state, error) {
    state.batchError = error;
  },
  CLEAR_BATCH_ERROR(state) {
    state.batchError = null;
  },
  // Summary mutations
  SET_SUMMARY(state, summary) {
    if (summary) {
      state.summary = {
        this_year: summary.this_year || { submitted: 0, rejected: 0, approved: 0, completed: 0, percentage: 0, total: 0 },
        history: summary.history || { submitted: 0, rejected: 0, approved: 0, completed: 0, percentage: 0, total: 0 }
      };
    }
  },
  SET_SUMMARY_LOADING(state, loading) {
    state.summaryLoading = loading;
  }
};

const actions = {
  async getCourses({ commit }, params) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      const response = await mitraUnenrollmentApi.getCourses(params);
      
      if (response.data.httpCode === 200) {
        commit('SET_COURSES', response.data.data.data);
        commit('SET_PAGINATION', {
          total: response.data.data.total || 0,
          page: response.data.data.page || 1,
          limit: response.data.data.limit || 10,
          totalPage: response.data.data.totalPage || 0
        });
        return response.data.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch courses');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async getCourseDetail({ commit }, id) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      const response = await mitraUnenrollmentApi.getCourseDetail(id);
      
      if (response.data.httpCode === 200) {
        commit('SET_COURSE_DETAIL', response.data.data);
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch course detail');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Get batches for cancellation
  async getBatches({ commit }, { courseId, params }) {
    console.log('📦 Store getBatches called with:', { courseId, params });
    
    commit('SET_BATCH_LOADING', true);
    commit('CLEAR_BATCH_ERROR');
    
    try {
      console.log('🌐 Calling API getBatches...');
      const response = await mitraUnenrollmentApi.getBatches(courseId, params);
      console.log('📥 API Response:', response);
      
      if (response.data.httpCode === 200) {
        const batches = normalizeBatchList(response.data.data);
        console.log('✅ Setting batches:', batches);
        commit('SET_BATCHES', batches);
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch batches');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      commit('SET_BATCH_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_BATCH_LOADING', false);
    }
  },

  // Cancel participants
  async cancelParticipants({ commit }, { batchId, data }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      const response = await mitraUnenrollmentApi.cancelParticipants(batchId, data);
      
      if (response.data.httpCode === 200) {
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to cancel participants');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Get summary statistics
  async getSummary({ commit }, params) {
    commit('SET_SUMMARY_LOADING', true);
    
    try {
      const response = await mitraUnenrollmentApi.getSummary(params);
      
      if (response.data.httpCode === 200) {
        commit('SET_SUMMARY', response.data.data);
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch summary');
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
      throw error;
    } finally {
      commit('SET_SUMMARY_LOADING', false);
    }
  },

  // Download bulk unenrollment template
  async downloadTemplate({ commit }, courseId) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      const response = await mitraUnenrollmentApi.downloadTemplate(courseId);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Bulk unenrollment via Excel upload
  async bulkUnenrollment({ commit }, { courseId, formData }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      const response = await mitraUnenrollmentApi.bulkUnenrollment(courseId, formData);
      
      if (response.data.httpCode === 200) {
        // Return full response including message
        return {
          message: response.data.message,
          data: response.data.data
        };
      } else {
        throw new Error(response.data.message || 'Failed to process bulk unenrollment');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
